/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import qs from 'query-string';
import axios from 'axios';
import { hooks, services, unstable, utils } from '@broadleaf/admin-components';
import {
  isDealerNetworkResolverType,
  isDefaultResolverType,
  isWhiteLabelResolverType
} from 'app/view/components/actions';
import { get, isEmpty, padStart } from 'lodash';

const { useCurrentApplication, useEventCallback } = hooks;
const { ClientDiscoveryService } = services;
const { useAuthContext, useCurrentTenant } = unstable;
const { Environment } = utils;
const { getClientDiscovery } = ClientDiscoveryService;

/**
 * Hook that returns a callback for initiating the impersonation flow for a user.
 *
 * If no username is provided, this will initiate the anonymous impersonation flow.
 *
 * @param {string} [username] the username, if one exists
 * @param {string} [urlResolverType] determines which url resolver to use, DEFAULT or WHITELABEL
 * @return {Function<Promise<void>>} the callback that initiates the impersonation flow
 */
export default function useImpersonateUser(username, urlResolverType) {
  const tenant = useCurrentTenant();
  const { clientId } = useAuthContext();
  const application = useCurrentApplication();

  return useEventCallback(async () => {
    await impersonate(username, clientId, tenant, application, urlResolverType);
  }, [application, clientId, username, tenant]);
}

async function getImpersonationParams(
  tenant,
  application,
  urlResolverType,
  clientId,
  username
) {
  const { targetApplication, dealerNetworkStoreNumber } = findTargetApplication(
    application,
    urlResolverType
  );
  const [{ clientId: impersonated_client_id }, { url: redirect_uri }] =
    await Promise.all([
      getClientDiscovery(tenant, targetApplication, 'APPLICATION'),
      getResolvedApplicationUrl(targetApplication.id, urlResolverType)
    ]);

  return {
    params: {
      client_id: clientId,
      impersonated_sub: username,
      impersonated_client_id,
      redirect_uri
    },
    dealerNetworkStoreNumber
  };
}

function findTargetApplication(application, urlResolverType) {
  const isDefault = isDefaultResolverType(urlResolverType);
  const isWhiteLabel = isWhiteLabelResolverType(urlResolverType);
  const isDealerNetwork = isDealerNetworkResolverType(urlResolverType);
  const dealerNetworkId = get(application, 'dealerNetworkId');
  const appIsDealerNetwork = get(application, 'dealerNetwork', false);

  // Use existing app for impersonate if:
  const useCurrentApp =
    isDefault || // Default impersonate
    isWhiteLabel || // White label impersonate
    appIsDealerNetwork || // Current app is dealer network
    (isDealerNetwork && isEmpty(dealerNetworkId)); // Dealer network type with no dealer network id (Should not happen)
  if (useCurrentApp) {
    return { targetApplication: application, dealerNetworkStoreNumber: null };
  }

  // Otherwise, we'll use the dealer network ID as the app to impersonate. Impersonate only requires the app's ID.
  const locationParam = buildLocationParam(application);
  return {
    targetApplication: { id: dealerNetworkId },
    dealerNetworkStoreNumber: locationParam
  };
}

function buildLocationParam(application) {
  const accountNumber = get(application, 'accountNumber');
  const locationNumber = get(application, 'locationNumber');
  if (isEmpty(accountNumber) || isEmpty(locationNumber)) {
    return null;
  }
  // return in the format D-NNNN-NN
  return `D-${padStart(accountNumber, 4, '0')}-${padStart(
    locationNumber,
    2,
    '0'
  )}`;
}

/**
 * Initiates the impersonation of the given user for the current tenant and application.
 *
 * @param {string} username (optional) the username of the user to be impersonated, if not impersonating guest
 * @param {string} clientId the client ID of the current admin client
 * @param {Tenant} tenant the current tenant
 * @param {Application} application the current application
 * @param {string} [urlResolverType] determines which url resolver to use, DEFAULT or WHITELABEL
 * @returns {Promise<void>}
 */
export async function impersonate(
  username,
  clientId,
  tenant,
  application,
  urlResolverType
) {
  const { params, dealerNetworkStoreNumber } = await getImpersonationParams(
    tenant,
    application,
    urlResolverType,
    clientId,
    username
  );
  let urlParams = params;
  if (dealerNetworkStoreNumber) {
    urlParams = { ...urlParams, dealerLocation: dealerNetworkStoreNumber };
  }
  const url = getImpersonationUrl() + '?' + qs.stringify(urlParams);
  const impersonationWindow = window.open(url);
  impersonationWindow.focus();
}

function getImpersonationUrl() {
  return Environment.get('auth.impersonate.url', '/auth/impersonate');
}

export async function getResolvedApplicationUrl(
  applicationId,
  urlResolverType
) {
  const isDefault = isDefaultResolverType(urlResolverType);
  const resolverUrl = isDefault
    ? getApplicationUrlDefaultResolverUrl()
    : getApplicationUrlResolverUrl();
  const { data } = await axios.get(resolverUrl, {
    params: {
      applicationId
    }
  });
  return data;
}

function getApplicationUrlResolverUrl() {
  return Environment.get(
    'tenant.urlresolver.application.url',
    '/api/tenant/url-resolver/application'
  );
}

function getApplicationUrlDefaultResolverUrl() {
  return Environment.get(
    'tenant.urlresolver.application.default.url',
    '/api/tenant/url-resolver/application/default'
  );
}
